/**
 * @file ShapeSamples.tsx
 * @description Образцы вкладки «Кнопки и карточки» (phase19.16 Р1; SB11;
 * uiux-design-r1 §1): строка «Смотреть каталог → / Подробнее →» и карточка
 * статьи — цветами и формами темы. Вынесены из `ShapesTab` (SB122,
 * группы-карточки). Решение 2026-10-12: образец «Карточек и фото» —
 * нейтральная карточка статьи (товар — образец только в каталоге).
 *
 * SB120 этап 2 (uiux-sb120-stage2 §2): образцы живые по числам темы —
 * скругление кнопок (`buttonRadiusCss`, «Пилюля» = 9999px), скругление
 * карточек px, рамка и тень по той же шкале, что у витрины (`cardSampleStyle`).
 */

import { buttonRadiusCss, cssPx, deriveSiteColors, type SiteColors, type SiteTheme } from "@chaiberi/shared";

import { cardSampleStyle, type CardLookValue } from "../../../lib/site-view/card-shapes";

type Shapes = SiteTheme["shapes"];

/** Подпись кнопки с учётом регистра темы. */
function caseText(shapes: Shapes, text: string): string {
  return shapes.buttonCase === "upper" ? text.toLocaleUpperCase("ru") : text;
}

/** «Смотреть каталог» и ссылка «Подробнее» на фоне страницы. */
export function ButtonsSample({ theme }: { theme: SiteTheme }) {
  const { colors, shapes } = theme;
  const arrow = shapes.buttonArrow ? " →" : "";
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl p-4" style={{ background: colors.pageBg }} aria-label="Образец кнопок">
      <span
        className="px-4 py-2 text-sm font-medium"
        style={{ background: colors.button, color: deriveSiteColors(colors).buttonText, borderRadius: buttonRadiusCss(shapes.buttonRadius) }}
      >
        {caseText(shapes, "Смотреть каталог")}
        {arrow}
      </span>
      <span className="text-sm font-medium" style={{ color: colors.accent }}>
        {caseText(shapes, "Подробнее")}
        {arrow}
      </span>
    </div>
  );
}

interface CardSampleProps {
  look: Pick<CardLookValue, "radius" | "cards">;
  colors: SiteColors;
  /** «Образец карточки статьи». */
  label: string;
  title: string;
  caption: string;
}

/** Карточка-образец (обложка, заголовок, подпись) на фоне страницы. */
export function CardSample({ look, colors, label, title, caption }: CardSampleProps) {
  return (
    <div className="rounded-xl p-4" style={{ background: colors.pageBg }}>
      <div className="flex w-44 max-w-full flex-col gap-2 p-3" aria-label={label} style={cardSampleStyle(look, colors)}>
        <span className="block h-20" style={{ background: colors.panelBg, borderRadius: cssPx(look.radius) }} />
        <span className="text-sm font-medium">{title}</span>
        <span className="text-xs" style={{ color: colors.textMuted }}>
          {caption}
        </span>
      </div>
    </div>
  );
}

/** Карточка статьи: скругление, рамка и тень карточек сайта. */
export function ArticleCardSample({ theme }: { theme: SiteTheme }) {
  const { colors, shapes } = theme;
  return <CardSample look={{ radius: shapes.radius.card, cards: shapes.cards }} colors={colors} label="Образец карточки статьи" title="Статья" caption="12 октября" />;
}
